import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
  IconButton,
  Tooltip,
  Chip,
  Stack,
} from "@mui/material";
import React, { useEffect, useState, useCallback, useRef } from "react";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ICategory from "../../entities/ICategory";
import ICategoryVariant from "../../entities/ICategoryVariant";
import CategoryService from "../../services/CategoryService";
import MediaService from "../../services/MediaService";
import { useParams, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { unwrapData, unwrapList, getErrorMessage, resolveImageUrl } from "../../services/apiUtils";
import { theme } from "../../theme";

type categoryProps = {
  isAdd?: boolean;
  isDelete?: boolean;
  catId?: number;
};

type parentCategoryOption = {
  categoryId: number;
  title: string;
};

const emptyCategory: ICategory = {
  categoryId: 0,
  title: "",
  description: "",
  parentId: 0,
  thumbnail: "",
};

const AddCategory: React.FC<categoryProps> = (): JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categoryId = Number(id) || 0;
  const isEditMode = categoryId > 0;

  const [initialValues, setInitialValues] = useState<ICategory>(emptyCategory);
  const [parentOptions, setParentOptions] = useState<Array<parentCategoryOption>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const [variants, setVariants] = useState<Array<ICategoryVariant>>([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [variantsError, setVariantsError] = useState<string | null>(null);
  const [newVariantName, setNewVariantName] = useState("");
  const [newVariantValues, setNewVariantValues] = useState("");
  const [savingVariant, setSavingVariant] = useState(false);

  const [addingOptionFor, setAddingOptionFor] = useState<number | null>(null);
  const [newOptionValue, setNewOptionValue] = useState("");
  const [savingOption, setSavingOption] = useState(false);

  const [editingVariantId, setEditingVariantId] = useState<number | null>(null);
  const [editingVariantName, setEditingVariantName] = useState("");
  const [savingVariantEdit, setSavingVariantEdit] = useState(false);

  const [editingOptionId, setEditingOptionId] = useState<number | null>(null);
  const [editingOptionValue, setEditingOptionValue] = useState("");
  const [savingOptionEdit, setSavingOptionEdit] = useState(false);

  const loadVariants = useCallback(() => {
    if (!isEditMode) {
      setVariants([]);
      return;
    }
    setVariantsLoading(true);
    setVariantsError(null);
    CategoryService.getCategoryVariants(categoryId)
      .then((list) => setVariants(list))
      .catch((err: any) => {
        setVariants([]);
        setVariantsError(getErrorMessage(err, "Could not load variants."));
      })
      .finally(() => setVariantsLoading(false));
  }, [categoryId, isEditMode]);

  useEffect(() => {
    loadVariants();
  }, [loadVariants]);

  const handleAddVariant = async () => {
    const name = newVariantName.trim();
    const values = newVariantValues
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    if (!name || values.length === 0) {
      setSnackbar({
        open: true,
        message: "Variant name and at least one value (comma-separated) are required.",
        severity: "error",
      });
      return;
    }
    setSavingVariant(true);
    try {
      const res: any = await CategoryService.addCategoryVariant(categoryId, name);
      const created = unwrapData(res);
      const categoryVariantId = created?.categoryVariantId;
      if (categoryVariantId) {

        for (let i = 0; i < values.length; i++) {
          await CategoryService.addCategoryVariantOption(categoryVariantId, values[i], i + 1);
        }
      }
      setNewVariantName("");
      setNewVariantValues("");
      loadVariants();
      setSnackbar({ open: true, message: "Variant added.", severity: "success" });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, "Could not add the variant."),
        severity: "error",
      });
    } finally {
      setSavingVariant(false);
    }
  };

  const handleDeleteVariant = async (v: ICategoryVariant) => {
    try {
      await CategoryService.deleteCategoryVariant(v.categoryVariantId);
      setVariants((prev) => prev.filter((x) => x.categoryVariantId !== v.categoryVariantId));
      setSnackbar({ open: true, message: "Variant removed.", severity: "success" });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, "Could not remove the variant."),
        severity: "error",
      });
    }
  };

  const handleDeleteOption = async (variant: ICategoryVariant, optionId: number) => {
    try {
      await CategoryService.deleteCategoryVariantOption(optionId);
      setVariants((prev) =>
        prev.map((v) =>
          v.categoryVariantId === variant.categoryVariantId
            ? {
                ...v,
                categoryVariantOptionList: v.categoryVariantOptionList.filter(
                  (o) => o.categoryVariantOptionId !== optionId
                ),
              }
            : v
        )
      );
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, "Could not remove the value."),
        severity: "error",
      });
    }
  };

  const handleAddOption = async (variant: ICategoryVariant) => {
    const value = newOptionValue.trim();
    if (!value) return;
    setSavingOption(true);
    try {
      await CategoryService.addCategoryVariantOption(
        variant.categoryVariantId,
        value,
        variant.categoryVariantOptionList.length + 1
      );
      setNewOptionValue("");
      setAddingOptionFor(null);
      loadVariants();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, "Could not add the value."),
        severity: "error",
      });
    } finally {
      setSavingOption(false);
    }
  };

  const startEditVariant = (v: ICategoryVariant) => {
    setEditingVariantId(v.categoryVariantId);
    setEditingVariantName(v.variantName);
  };

  const cancelEditVariant = () => {
    setEditingVariantId(null);
    setEditingVariantName("");
  };

  const saveEditVariant = async (v: ICategoryVariant) => {
    const name = editingVariantName.trim();
    if (!name) return;
    if (name === v.variantName) {
      cancelEditVariant();
      return;
    }
    setSavingVariantEdit(true);
    try {
      await CategoryService.updateCategoryVariant({ ...v, variantName: name });
      setVariants((prev) =>
        prev.map((x) => (x.categoryVariantId === v.categoryVariantId ? { ...x, variantName: name } : x))
      );
      cancelEditVariant();
      setSnackbar({ open: true, message: "Variant name updated.", severity: "success" });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, "Could not update the variant name."),
        severity: "error",
      });
    } finally {
      setSavingVariantEdit(false);
    }
  };

  const startEditOption = (opt: ICategoryVariant["categoryVariantOptionList"][number]) => {
    setEditingOptionId(opt.categoryVariantOptionId);
    setEditingOptionValue(opt.optionValue);
  };

  const cancelEditOption = () => {
    setEditingOptionId(null);
    setEditingOptionValue("");
  };

  const saveEditOption = async (
    variant: ICategoryVariant,
    opt: ICategoryVariant["categoryVariantOptionList"][number]
  ) => {
    const value = editingOptionValue.trim();
    if (!value) return;
    if (value === opt.optionValue) {
      cancelEditOption();
      return;
    }
    setSavingOptionEdit(true);
    try {
      await CategoryService.updateCategoryVariantOption(
        opt.categoryVariantOptionId,
        variant.categoryVariantId,
        value,
        opt.displayOrder
      );
      setVariants((prev) =>
        prev.map((v) =>
          v.categoryVariantId === variant.categoryVariantId
            ? {
                ...v,
                categoryVariantOptionList: v.categoryVariantOptionList.map((o) =>
                  o.categoryVariantOptionId === opt.categoryVariantOptionId ? { ...o, optionValue: value } : o
                ),
              }
            : v
        )
      );
      cancelEditOption();
      setSnackbar({ open: true, message: "Value updated.", severity: "success" });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, "Could not update the value."),
        severity: "error",
      });
    } finally {
      setSavingOptionEdit(false);
    }
  };

  useEffect(() => {
    CategoryService.getParentCategory()
      .then((res: any) => {
        const list = unwrapList<ICategory>(res);
        setParentOptions(
          list
            .filter((cat) => cat.categoryId !== categoryId)
            .map((cat) => ({ categoryId: cat.categoryId, title: cat.title || "" }))
        );
      })
      .catch(() => {});
  }, [categoryId]);

  useEffect(() => {
    if (!isEditMode) {
      setInitialValues(emptyCategory);
      setImagePreview("");
      setImageLoadFailed(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    CategoryService.getCategoryById(categoryId)
      .then((res: any) => {
        const category = unwrapData(res) as ICategory;
        setInitialValues({
          categoryId: category?.categoryId ?? categoryId,
          title: category?.title ?? "",
          description: category?.description ?? "",
          parentId: category?.parentId ?? 0,
          thumbnail: category?.thumbnail ?? "",
        });
        setImagePreview(category?.thumbnail || "");
        setImageLoadFailed(false);
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not load this category."),
          severity: "error",
        });
      })
      .finally(() => setIsLoading(false));
  }, [categoryId, isEditMode]);

  const handlePickFile = (file: File | undefined | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setSnackbar({ open: true, message: "Please choose an image file.", severity: "error" });
      return;
    }
    setImageFile(file);
    setImageLoadFailed(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    setImageLoadFailed(false);
  };

  const uploadImageIfNeeded = useCallback(async (): Promise<string | undefined> => {
    if (!imageFile) return undefined;
    setUploadProgress(0);
    try {
      const res: any = await MediaService.uploadSingle(imageFile, undefined, (evt: any) => {
        if (evt?.total) {
          setUploadProgress(Math.round((100 * evt.loaded) / evt.total));
        }
      });
      const uploaded = unwrapData(res) as any;
      return uploaded?.col1 || uploaded?.dataUrl || undefined;
    } finally {
      setUploadProgress(null);
    }
  }, [imageFile]);

  const handleFormSubmit = useCallback(
    async (values: ICategory, { setSubmitting }: any) => {
      try {
        let thumbnail = values.thumbnail;
        try {
          const uploadedUrl = await uploadImageIfNeeded();
          if (uploadedUrl) thumbnail = uploadedUrl;
        } catch (imgErr: any) {
          setSnackbar({
            open: true,
            message: getErrorMessage(imgErr, "Image upload failed. Category was not saved — please try again."),
            severity: "error",
          });
          setSubmitting(false);
          return;
        }

        const payload: ICategory = { ...values, parentId: values.parentId || 0, thumbnail };

        if (isEditMode) {
          await CategoryService.updateCategory(categoryId, payload);
        } else {
          await CategoryService.createCategory(payload);
        }

        setSnackbar({
          open: true,
          message: isEditMode ? "Category updated successfully." : "Category created successfully.",
          severity: "success",
        });
        setTimeout(() => navigate("/categories"), 600);
      } catch (err: any) {
        setSnackbar({
          open: true,
          message: getErrorMessage(
            err,
            isEditMode
              ? "Could not update the category. Please try again."
              : "Could not create the category. Please try again."
          ),
          severity: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [categoryId, isEditMode, navigate, uploadImageIfNeeded]
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      enableReinitialize
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={checkoutSchema}
    >
      {({ values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          <Box
            display="grid"
            gridTemplateColumns={isNonMobile ? "1fr 300px" : "1fr"}
            gridAutoRows="minmax(100px, auto)"
            gap="16px"
          >
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow:
                  "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  background: "linear-gradient(135deg, #303398 0%, #4F55D6 100%)",
                  color: "#fff",
                  px: 2.5,
                  py: 1.75,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <CategoryRoundedIcon />
                <Typography variant="subtitle1" fontWeight={600}>
                  Category Information
                </Typography>
              </Box>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  p: 2.5,
                  "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                }}
              >
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ gridColumn: isNonMobile ? "1 / 2" : undefined, padding: "10px 0 10px 0", m: "5px" }}
                >
                  Name
                </Typography>
                <TextField
                  fullWidth
                  hiddenLabel
                  id="outlined-number"
                  type="text"
                  name="title"
                  value={values.title}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!touched.title && !!errors.title}
                  helperText={touched.title && errors.title}
                  sx={{ gridColumn: isNonMobile ? "2 / 4" : undefined }}
                />
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ gridColumn: isNonMobile ? "1 / 2" : undefined, padding: "10px 0 10px 0", m: "5px" }}
                >
                  Description
                </Typography>
                <TextField
                  fullWidth
                  hiddenLabel
                  id="description"
                  type="text"
                  name="description"
                  value={values.description}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  sx={{ gridColumn: isNonMobile ? "2 / 4" : undefined }}
                />
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ gridColumn: isNonMobile ? "1 / 2" : undefined, padding: "10px 0 10px 0", m: "5px" }}
                >
                  Parent Category
                </Typography>
                <FormControl sx={{ gridColumn: isNonMobile ? "2 / 4" : undefined }} fullWidth>
                  <InputLabel id="parent-category-label" shrink>
                    Parent Category
                  </InputLabel>
                  <Select
                    labelId="parent-category-label"
                    label="Parent Category"
                    name="parentId"
                    value={values.parentId || 0}
                    onChange={(e: SelectChangeEvent<number>) =>
                      setFieldValue("parentId", Number(e.target.value))
                    }
                    onBlur={handleBlur}
                    displayEmpty
                  >
                    <MenuItem value={0}>-- none (top-level category) --</MenuItem>
                    {parentOptions.map((cat) => (
                      <MenuItem key={cat.categoryId} value={cat.categoryId}>
                        {cat.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow:
                  "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
                overflow: "hidden",
                height: "fit-content",
              }}
            >
              <Box
                sx={{
                  background: "linear-gradient(135deg, #303398 0%, #4F55D6 100%)",
                  color: "#fff",
                  px: 2.5,
                  py: 1.75,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <ImageRoundedIcon />
                <Typography variant="subtitle1" fontWeight={600}>
                  Category Image
                </Typography>
              </Box>

              <Box sx={{ p: 2.5 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handlePickFile(e.target.files?.[0])}
                />

                <Box
                  onClick={() => !imagePreview && fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    handlePickFile(e.dataTransfer.files?.[0]);
                  }}
                  sx={{
                    position: "relative",
                    borderRadius: "12px",
                    border: `2px dashed ${isDragging ? theme.palette.primary.main : "#ccc"}`,
                    backgroundColor: isDragging ? theme.palette.primary.light : "#fafafa",
                    minHeight: 200,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: imagePreview ? "default" : "pointer",
                    overflow: "hidden",
                    transition: "all 0.15s ease",
                  }}
                >
                  {imagePreview && !imageLoadFailed ? (
                    <>
                      <img
                        src={resolveImageUrl(imagePreview)}
                        alt="Category thumbnail"
                        onError={() => setImageLoadFailed(true)}
                        style={{ width: "100%", height: 200, objectFit: "cover", display: "block" }}
                      />
                      <Tooltip title="Remove image">
                        <IconButton
                          size="small"
                          onClick={handleRemoveImage}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                          }}
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : imagePreview && imageLoadFailed ? (
                    <Box textAlign="center" px={2}>
                      <ImageRoundedIcon sx={{ fontSize: 36, color: "grey.400", mb: 1 }} />
                      <Typography variant="body2" color="error">
                        Could not load the image (the URL may be invalid or unreachable).
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: "break-all", display: "block", mb: 1 }}
                      >
                        {resolveImageUrl(imagePreview)}
                      </Typography>
                      <Button size="small" onClick={() => fileInputRef.current?.click()}>
                        Upload a new image
                      </Button>
                    </Box>
                  ) : (
                    <Box textAlign="center" px={2}>
                      <CloudUploadRoundedIcon sx={{ fontSize: 36, color: theme.palette.primary.main, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Click or drag an image here
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PNG or JPG, up to a few MB
                      </Typography>
                    </Box>
                  )}
                </Box>

                {uploadProgress !== null && (
                  <Box mt={1.5}>
                    <LinearProgress variant="determinate" value={uploadProgress} color="secondary" />
                    <Typography variant="caption" color="text.secondary">
                      Uploading image… {uploadProgress}%
                    </Typography>
                  </Box>
                )}

                {imagePreview && (
                  <Button
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ mt: 1.5 }}
                  >
                    Change image
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {isEditMode && (
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow:
                  "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
                overflow: "hidden",
                mt: "16px",
              }}
            >
              <Box
                sx={{
                  background: "linear-gradient(135deg, #303398 0%, #4F55D6 100%)",
                  color: "#fff",
                  px: 2.5,
                  py: 1.75,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <TuneRoundedIcon />
                <Typography variant="subtitle1" fontWeight={600}>
                  Variants
                </Typography>
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Every product created in this category will be able to pick from these
                  variant options (e.g. Color, Size). If no variant is added here, the
                  product form for this category won't show a variants option at all.
                  Click a value to edit it, or use the pencil icon to rename the variant.
                </Typography>

                {variantsLoading ? (
                  <CircularProgress size={22} />
                ) : (
                  <Stack spacing={1.5} mb={2.5}>
                    {variantsError && (
                      <Alert severity="error" onClose={() => setVariantsError(null)}>
                        {variantsError}{" "}
                        <Button size="small" onClick={loadVariants} sx={{ ml: 1 }}>
                          Retry
                        </Button>
                      </Alert>
                    )}
                    {!variantsError && variants.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        This category doesn't have any variant options yet.
                      </Typography>
                    )}
                    {variants.map((v) => (
                      <Box
                        key={v.categoryVariantId}
                        sx={{ border: "1px solid #eee", borderRadius: "10px", p: 1.25 }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          {editingVariantId === v.categoryVariantId ? (
                            <Stack direction="row" spacing={0.5} alignItems="center" flex={1}>
                              <TextField
                                size="small"
                                autoFocus
                                value={editingVariantName}
                                onChange={(e) => setEditingVariantName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    saveEditVariant(v);
                                  } else if (e.key === "Escape") {
                                    cancelEditVariant();
                                  }
                                }}
                                sx={{ width: 160 }}
                              />
                              <Tooltip title="Save">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={savingVariantEdit}
                                    onClick={() => saveEditVariant(v)}
                                  >
                                    <CheckRoundedIcon fontSize="small" color="success" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={cancelEditVariant}>
                                  <CloseRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : (
                            <Typography variant="subtitle2" fontWeight={600}>
                              {v.variantName}
                            </Typography>
                          )}
                          <Box display="flex" alignItems="center">
                            {editingVariantId !== v.categoryVariantId && (
                              <Tooltip title="Rename this variant">
                                <IconButton size="small" onClick={() => startEditVariant(v)}>
                                  <EditRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Remove this whole variant">
                              <IconButton size="small" onClick={() => handleDeleteVariant(v)}>
                                <DeleteOutlineRoundedIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.5} alignItems="center">
                          {v.categoryVariantOptionList.map((opt) =>
                            editingOptionId === opt.categoryVariantOptionId ? (
                              <Stack
                                key={opt.categoryVariantOptionId}
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                mb={0.5}
                              >
                                <TextField
                                  size="small"
                                  autoFocus
                                  value={editingOptionValue}
                                  onChange={(e) => setEditingOptionValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      saveEditOption(v, opt);
                                    } else if (e.key === "Escape") {
                                      cancelEditOption();
                                    }
                                  }}
                                  sx={{ width: 120 }}
                                />
                                <Tooltip title="Save">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={savingOptionEdit}
                                      onClick={() => saveEditOption(v, opt)}
                                    >
                                      <CheckRoundedIcon fontSize="small" color="success" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton size="small" onClick={cancelEditOption}>
                                    <CloseRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            ) : (
                              <Chip
                                key={opt.categoryVariantOptionId}
                                label={opt.optionValue}
                                size="small"
                                sx={{ mb: 0.5, cursor: "pointer" }}
                                onClick={() => startEditOption(opt)}
                                onDelete={() => handleDeleteOption(v, opt.categoryVariantOptionId)}
                              />
                            )
                          )}
                          {addingOptionFor === v.categoryVariantId ? (
                            <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                              <TextField
                                size="small"
                                autoFocus
                                placeholder="e.g. Purple"
                                value={newOptionValue}
                                onChange={(e) => setNewOptionValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddOption(v);
                                  }
                                }}
                                sx={{ width: 130 }}
                              />
                              <Button
                                size="small"
                                variant="text"
                                disabled={savingOption}
                                onClick={() => handleAddOption(v)}
                              >
                                Add
                              </Button>
                            </Stack>
                          ) : (
                            <Chip
                              icon={<AddRoundedIcon fontSize="small" />}
                              label="Add value"
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setAddingOptionFor(v.categoryVariantId);
                                setNewOptionValue("");
                              }}
                              sx={{ mb: 0.5 }}
                            />
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}

                <Box
                  display="grid"
                  gridTemplateColumns={isNonMobile ? "1fr 1fr auto" : "1fr"}
                  gap="12px"
                  alignItems="start"
                >
                  <TextField
                    label="Variant name"
                    placeholder="e.g. Color"
                    size="small"
                    value={newVariantName}
                    onChange={(e) => setNewVariantName(e.target.value)}
                  />
                  <TextField
                    label="Values (comma separated)"
                    placeholder="e.g. Red, Blue, Green"
                    size="small"
                    value={newVariantValues}
                    onChange={(e) => setNewVariantValues(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AddRoundedIcon />}
                    disabled={savingVariant}
                    onClick={handleAddVariant}
                    sx={{ height: 40 }}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          <Box display="flex" justifyContent="end" mt="20px">
            <Button
              color="secondary"
              variant="contained"
              type="submit"
              disabled={isSubmitting}
              startIcon={<SaveRoundedIcon />}
              sx={{ borderRadius: "10px", px: 3, boxShadow: "0px 4px 10px rgba(33,150,243,0.35)" }}
            >
              {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Create"}
            </Button>
          </Box>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </form>
      )}
    </Formik>
  );
};

const checkoutSchema = yup.object().shape({
  title: yup.string().trim().required("Name is required"),
});

export default AddCategory;
